import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, XCircle, Clock, ArrowRight, Building2, Copy, Check } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PICKUP_ADDRESS = '17 rue du Dépôt, 01500 Ambérieu-en-Bugey';

const CopyButton = ({ value, label }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} copié`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copie impossible');
    }
  };
  return (
    <button onClick={copy} className="text-gray-400 hover:text-[#E42313] p-1" type="button" aria-label={`Copier ${label}`}>
      {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
    </button>
  );
};

const TransferConfirmation = ({ data }) => (
  <div className="max-w-2xl w-full bg-white rounded-lg shadow-sm border border-gray-200 p-8 md:p-10">
    <div className="text-center mb-8">
      <div className="inline-flex w-16 h-16 bg-blue-100 rounded-full items-center justify-center mb-4">
        <Building2 className="w-8 h-8 text-blue-600" />
      </div>
      <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="transfer-success-title">
        Commande enregistrée !
      </h1>
      <p className="text-gray-600">
        Effectuez votre virement avec les coordonnées ci-dessous. Votre commande sera préparée dès
        réception du paiement.
      </p>
    </div>

    <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
      <div className="flex justify-between items-center mb-4 pb-4 border-b border-gray-200">
        <div>
          <div className="text-xs text-gray-500">N° de commande</div>
          <div className="font-mono font-bold text-[#E42313] text-lg" data-testid="transfer-order-number">
            {data.order_number}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-500">Montant à virer</div>
          <div className="font-bold text-2xl">{data.amount?.toFixed(2)} €</div>
        </div>
      </div>

      <h3 className="font-bold text-gray-900 mb-3">Coordonnées bancaires</h3>
      <table className="w-full text-sm">
        <tbody>
          <tr className="border-b border-gray-100">
            <td className="py-2 text-gray-500 w-32">Bénéficiaire</td>
            <td className="py-2 font-semibold">{data.holder}</td>
            <td className="py-2 text-right"><CopyButton value={data.holder} label="Bénéficiaire" /></td>
          </tr>
          <tr className="border-b border-gray-100">
            <td className="py-2 text-gray-500">Banque</td>
            <td className="py-2">{data.bank}</td>
            <td></td>
          </tr>
          <tr className="border-b border-gray-100">
            <td className="py-2 text-gray-500">IBAN</td>
            <td className="py-2 font-mono text-xs sm:text-sm break-all">{data.iban}</td>
            <td className="py-2 text-right"><CopyButton value={data.iban?.replace(/\s/g, '')} label="IBAN" /></td>
          </tr>
          <tr className="border-b border-gray-100">
            <td className="py-2 text-gray-500">BIC / SWIFT</td>
            <td className="py-2 font-mono">{data.bic}</td>
            <td className="py-2 text-right"><CopyButton value={data.bic} label="BIC" /></td>
          </tr>
          <tr>
            <td className="py-2 text-gray-500">Référence</td>
            <td className="py-2 font-mono font-semibold">{data.order_number}</td>
            <td className="py-2 text-right"><CopyButton value={data.order_number} label="Référence" /></td>
          </tr>
        </tbody>
      </table>
      <p className="text-xs text-gray-500 mt-4">
        ⚠️ Pensez à indiquer <strong>{data.order_number}</strong> en référence de votre virement.
      </p>
    </div>

    <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded mb-6 text-sm text-amber-900">
      <strong>Retrait sur place :</strong> {PICKUP_ADDRESS}<br />
      Vous serez prévenu(e) par email dès que votre commande est prête.
    </div>

    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <Link to="/mon-compte" className="bg-[#E42313] text-white px-6 py-3 rounded font-semibold hover:bg-[#C11A0F] text-center">
        Voir mes commandes
      </Link>
      <Link to="/produits" className="border border-gray-300 px-6 py-3 rounded font-semibold hover:bg-gray-50 text-center">
        Continuer mes achats
      </Link>
    </div>
  </div>
);

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const location = useLocation();
  const sessionId = params.get('session_id');
  const transferData = location.state?.type === 'transfer' ? location.state : null;

  const [status, setStatus] = useState(transferData ? 'transfer' : 'polling');
  const [orderNumber, setOrderNumber] = useState(null);
  const [amount, setAmount] = useState(null);
  const { loadCart } = useCart();
  const pollingRef = useRef(0);

  useEffect(() => {
    if (transferData) return;
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const poll = async () => {
      if (pollingRef.current >= 10) {
        setStatus('timeout');
        return;
      }
      pollingRef.current += 1;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        setOrderNumber(data.order_number);
        if (data.amount_total) setAmount(data.amount_total / 100);
        if (data.payment_status === 'paid') {
          setStatus('paid');
          loadCart();
          return;
        }
        if (data.status === 'expired') {
          setStatus('expired');
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setStatus('error');
      }
    };

    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  if (status === 'transfer') {
    return (
      <div className="min-h-[80vh] bg-gray-50 flex items-center justify-center px-4 py-12">
        <TransferConfirmation data={transferData} />
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] bg-gray-50 flex items-center justify-center px-4 py-12">
      <div className="max-w-xl w-full bg-white rounded-lg shadow-sm border border-gray-200 p-10 text-center">
        {status === 'polling' && (
          <>
            <Clock className="w-16 h-16 mx-auto text-amber-500 mb-4 animate-pulse" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Vérification du paiement…</h1>
            <p className="text-gray-600">Merci de patienter quelques instants.</p>
          </>
        )}
        {status === 'paid' && (
          <>
            <CheckCircle2 className="w-16 h-16 mx-auto text-green-600 mb-4" />
            <h1 className="text-3xl font-bold text-gray-900 mb-3" data-testid="payment-success-title">
              Paiement confirmé !
            </h1>
            <p className="text-gray-600 mb-4">
              Un email de confirmation vous a été envoyé. Votre commande sera disponible au retrait
              à : <strong>{PICKUP_ADDRESS}</strong>
            </p>
            {orderNumber && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                <div className="text-xs text-gray-500">N° de commande</div>
                <div className="font-mono font-bold text-[#E42313] text-lg" data-testid="payment-order-number">
                  {orderNumber}
                </div>
                {amount != null && <div className="text-sm text-gray-700 mt-1">Montant : {amount.toFixed(2)} €</div>}
              </div>
            )}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/mon-compte" className="bg-[#E42313] text-white px-6 py-3 rounded hover:bg-[#C11A0F] font-semibold">
                Voir mes commandes
              </Link>
              <Link to="/produits" className="border border-gray-300 px-6 py-3 rounded hover:bg-gray-50 font-semibold flex items-center justify-center gap-2">
                Continuer <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </>
        )}
        {(status === 'expired' || status === 'error' || status === 'timeout') && (
          <>
            <XCircle className="w-16 h-16 mx-auto text-red-600 mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {status === 'expired' ? 'Session expirée' : 'Paiement en attente'}
            </h1>
            <p className="text-gray-600 mb-6">
              {status === 'expired'
                ? 'La session de paiement a expiré. Vous pouvez recommencer.'
                : "Nous n'avons pas pu confirmer votre paiement. Vérifiez vos emails ou votre espace client."}
            </p>
            <Link to="/panier" className="bg-[#E42313] text-white px-6 py-3 rounded hover:bg-[#C11A0F] font-semibold">
              Retour au panier
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
